import { ReactNode } from 'react';
import styled from 'styled-components';

import { Box } from 'components/Box';
import { Button } from 'components/Button/Button';
import { Hide } from 'components/Hide';
import { StyledNativeLinkWithRef } from 'components/Link/StyledNativeLinkWithRef';
import { IdealistLogoOnDemand } from 'components/Logo/IdealistLogoOnDemand';
import { H4 } from 'components/Text/H4';
import { FlashMessageContainer } from 'containers/FlashMessageContainer';
import ShowConfirmationModal from 'containers/ShowConfirmationModal';
import { colors } from 'theme/theme';
import { trackAnchorClicked } from 'utils/analytics/track/trackAnchorClicked';
import { getItem as getLocalStorageItem } from 'utils/localStorage';
import { hideModal } from 'zustand-stores/modalStore';

const testMode = getLocalStorageItem('testMode');

type Props = {
  goBack: (saveDraft?: boolean) => void;
  otherLinks?: Array<ReactNode>;
  edited?: boolean;
  trackingTitle?: string;
  title?: string;
  children?: ReactNode;
  isDraft?: boolean;
  cartPill?: ReactNode;
};

function CloseButton({
  edited,
  goBack,
  isDraft,
}: {
  edited: boolean;
  goBack: (saveDraft?: boolean) => void;
  isDraft?: boolean;
}) {
  if (isDraft && edited) {
    return (
      <ShowConfirmationModal
        data-qa-id="multi-step-save-draft-modal"
        type="warning"
        title={getText('Do you want to save your draft?')}
        buttonText={getText('Save Draft')}
        extraButtons={
          <Button
            type="button"
            variant="danger"
            onClick={() => {
              hideModal();
              goBack(false);
            }}
            data-qa-id="discard-button"
          >
            {getText('Continue Without Saving')}
          </Button>
        }
        onConfirm={() => Promise.resolve(goBack(true))}
        render={({ showModal }) => (
          <Button
            type="button"
            onClick={() => showModal()}
            title={getText('Cancel')}
            data-qa-id="multi-form-back"
            tabIndex={0}
            icon={{ type: 'normalized', name: 'close' }}
            variant="tertiary-grey"
          />
        )}
      />
    );
  }

  return (
    <ShowConfirmationModal
      data-qa-id="multi-step-back-modal"
      type="danger"
      title={getText('Are you sure you want to go back?')}
      buttonText={getText('Yes, go back')}
      body={getText('If you go back, your data will not be saved.')}
      onConfirm={() => Promise.resolve(goBack(false))}
      render={({ showModal }) => (
        <Button
          type="button"
          onClick={() => (edited ? showModal() : goBack(false))}
          title={getText('Cancel')}
          data-qa-id="multi-form-back"
          icon={{ type: 'normalized', name: 'close' }}
          variant="tertiary-grey"
          tabIndex={0}
        />
      )}
    />
  );
}

const HeaderRow = styled.div`
  padding: 20px 32px;
  border-bottom: 1px solid #eaeaea;
  position: relative;
  display: flex;
  justify-content: space-between;
`;

export function MultiStepFormBaseHeader({
  goBack,
  otherLinks,
  edited,
  trackingTitle,
  title,
  isDraft,
  children,
  cartPill,
}: Props) {
  return (
    <Box
      position={testMode ? 'relative' : 'fixed'}
      top={0}
      left={0}
      width="100%"
      bg="white"
      zIndex={100}
    >
      <HeaderRow>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Hide xs sm>
            <Box display="flex" justifyContent="center">
              <Box display="flex" justifyContent="center" width={158}>
                <StyledNativeLinkWithRef
                  href="/"
                  title="Idealist"
                  onClick={(event) =>
                    trackAnchorClicked(
                      event,
                      `Idealist Logo Header - ${
                        title ? `${trackingTitle}` : 'Multi Step Form'
                      }`,
                    )
                  }
                  data-qa-id="header-logo-link"
                >
                  <IdealistLogoOnDemand
                    withText
                    height={{ mobile: 28, desktop: 36 }}
                  />
                </StyledNativeLinkWithRef>
              </Box>
            </Box>
          </Hide>
        </Box>
        {title && (
          <H4 m={0} textAlign={['right', 'center']}>
            {title}
          </H4>
        )}

        <div style={{ display: 'flex', alignItems: 'center' }}>
          {cartPill}

          <CloseButton
            goBack={goBack}
            edited={Boolean(edited)}
            isDraft={isDraft}
          />
        </div>
      </HeaderRow>

      {otherLinks && (
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          width={1}
          borderBottom="1px solid #eaeaea"
        >
          {otherLinks.map((link, index) => (
            <Box // eslint-disable-next-line react/no-array-index-key
              key={index}
              px={32}
              height={52}
              display="flex"
              justifyContent="center"
              alignItems="center"
              // @ts-expect-error TS(2322): Type 'string | false' is not assignable to type 'R... Remove this comment to see the full error message
              border={index > 0 && `1px solid ${colors.selectionGrey}`}
            >
              {link}
            </Box>
          ))}
        </Box>
      )}

      <FlashMessageContainer />

      {children}
    </Box>
  );
}
